import { TextStyle } from 'pixi.js'
import { useCallback } from 'react'
import { Container, Graphics, Text } from '@pixi/react'
import { useRealityCheckStore } from '../../domain/realityCheck/store/realityCheckStore'
import { useEffect } from 'react'
import { Draw } from '../../type/PixiType'
import { Button, FixedContainerV2 } from '../../UI'
import { leaveGameToLobby } from '../../domain/leaveGame/leaveGameUtil'
import { betHistoryServiceV2 } from '../../domain/betHistory'
import { useBetHistoryStore } from '../../domain/betHistory/store/betHistoryStore'
import { ga4Info } from '../../util/GA4'
import { Ga4EventType } from '../../util/GA4/Ga4EventType'
import { useBalanceFeatureToggleV2 } from '../../domain/balance/hook/useBalanceFeatureToggleV2'
import { useFeatureToggleV2 } from '../../domain/featureToggle/hooks/useFeatureToggleV2'
import { FeatureToggleV2Type } from '../../domain/featureToggle/enum/FeatureToggleType'
import { t } from '../../domain/language/languageUtil'

interface RealityCheckPopupProps {
  gameWidth: number
  gameHeight: number
  width?: number
  height?: number
  /** Continue 按鈕的尺寸，預設 300x84。畫面邏輯寬度比彈窗預設寬度（816）窄很多的原生直向遊戲，
   * 應該連同 width/height 一起等比例縮小，否則按鈕會相對彈窗顯得過大 */
  continueButtonWidth?: number
  continueButtonHeight?: number
}

export function RealityCheckPopupFeatureToggleV2({ gameWidth, gameHeight, width = 816, height = 515, continueButtonWidth = 300, continueButtonHeight = 84 }: RealityCheckPopupProps) {
  const { isShowPopup, intervalStartTime, hidePopup, resetTimer, interval, showPopup, popupCount } = useRealityCheckStore()
  const enableSessionTotalStake = useFeatureToggleV2(FeatureToggleV2Type.enableSessionTotalStake)
  const { currency, sessionTotalStake } = useBalanceFeatureToggleV2()

  const x = (gameWidth - width) / 2
  const y = (gameHeight - height) / 2

  const secondsPlayed = Math.floor(popupCount * (interval / 60000))
  const message = t('reality_check_message').replace('{minutes}', String(secondsPlayed))

  const backgroundDraw = useCallback<Draw>((background) => {
    background.clear()
    background.beginFill('#0D0A02')
    background.drawRoundedRect(0, 0, width - 4, height, 10)
  }, [height, width])

  const borderDraw = useCallback<Draw>((border) => {
    const borderWidth = 2
    const borderRadius = 10
    border.clear()
    border.lineStyle(borderWidth, '#DBD2CA', 1)
    border.drawRoundedRect(-borderWidth, 0, width - borderWidth, height - borderWidth, borderRadius)
  }, [height, width])

  const onContinue = () => {
    hidePopup()
    resetTimer()
  }

  const onHistory = () => {
    onContinue()
    betHistoryServiceV2.getBetList()
    useBetHistoryStore.setState({
      isOpened: true,
    })
    ga4Info.addEvent(Ga4EventType.menu_history)
  }

  const onLeaveGame = () => {
    leaveGameToLobby()
    ga4Info.addEvent(Ga4EventType.menu_lobby)
  }

  useEffect(() => {
    if (interval <= 0) return

    const timer = setInterval(() => {
      const elapsed = Date.now() - intervalStartTime
      if (elapsed >= interval && !isShowPopup) {
        showPopup()
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [interval, intervalStartTime, isShowPopup, showPopup])

  return isShowPopup && (
    <FixedContainerV2 name="RealityCheckPopup" width={gameWidth} height={gameHeight} background="0x000000" backgroundAlpha={0.6} blockEvents={true} eventMode="static">
      <Container position={[x, y]}>
        <Graphics draw={backgroundDraw} />
        <Graphics draw={borderDraw} />

        <Text
          text={message}
          x={width / 2}
          y={height * 0.4}
          anchor={0.5}
          style={
            new TextStyle({
              fontSize: 40,
              fontWeight: 'bold',
              fontFamily: 'Arial',
              fill: '#F0E1D4',
              align: 'center',
              wordWrap: true,
              breakWords: true,
              wordWrapWidth: width * 0.8,
              lineHeight: 50,
            })
          }
        />

        {enableSessionTotalStake && (
          <Text
            text={`${t('session_total_stake')}: ${sessionTotalStake} ${currency}`}
            x={width / 2}
            y={height * 0.55}
            anchor={0.5}
            style={
              new TextStyle({
                fontSize: 24,
                fontFamily: 'Arial',
                fill: '#F0E1D4',
              })
            }
          />
        )}

        <Container name="Buttons" y={height * 0.65}>
          {/* Leave Game Button */}
          <Button
            name="LeaveGameButton"
            x={width * 0.05}
            y={50}
            width={150}
            height={50}
            onPress={onLeaveGame}
            defaultView="error_popup_btn_normal"
            hoverView="error_popup_btn_hover"
            pressedView="error_popup_btn_hover"
          />
          <Text
            text={t('reality_check_exit_btn')}
            x={width * 0.05 + 75}
            y={73}
            anchor={0.5}
            eventMode="passive"
            style={
              new TextStyle({
                fontSize: 30,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#F0E1D4',
              })
            }
          />

          {/* Continue Button */}
          <Button
            name="ContinueButton"
            x={(width / 2) - continueButtonWidth / 2}
            y={0}
            width={continueButtonWidth}
            height={continueButtonHeight}
            onPress={onContinue}
            defaultView="error_popup_btn_normal"
            hoverView="error_popup_btn_hover"
            pressedView="error_popup_btn_hover"
          />
          <Text
            text={t('reality_check_continue_btn')}
            x={(width / 2)}
            y={continueButtonHeight / 2}
            anchor={0.5}
            eventMode="passive"
            style={
              new TextStyle({
                fontSize: continueButtonHeight * (36 / 84),
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#F0E1D4',
              })
            }
          />

          {/* History Button */}
          <Button
            name="HistoryButton"
            x={width * 0.85 - 75}
            y={50}
            width={150}
            height={50}
            onPress={onHistory}
            defaultView="error_popup_btn_normal"
            hoverView="error_popup_btn_hover"
            pressedView="error_popup_btn_hover"
          />
          <Text
            text={t('reality_check_history_btn')}
            x={width * 0.85}
            y={73}
            anchor={0.5}
            eventMode="passive"
            style={
              new TextStyle({
                fontSize: 30,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#F0E1D4',
              })
            }
          />
        </Container>
      </Container>
    </FixedContainerV2>
  )
}
